import { Inject, Injectable } from '@angular/core';
import { IHeroLocationData, locationData } from '../../data/location.data';
import { languageData } from '../../data/language.data';
import { tap, BehaviorSubject } from 'rxjs';
import {HERO_DEFAULT_LANGUAGE, HERO_DEFAULT_LOCATION} from "../token";


export interface IHeroLocale {
    code: string;
    name: string;
    native: string;
    currency: string;
    timezone: string[];
    flag: string;
    language: IHeroLanguage;
}

export interface IHeroLocation {
    code: string;
    name: string;
    native: string;
    currency: string;
    timezone: string[];
    flag: string;
    languages: IHeroLanguage[];
}

export interface IHeroLanguage{
    code: string;
    name: string;
    native: string;
}


@Injectable({
    providedIn: 'root'
})
export class HeroLocalizationService {

    /**
     * current locale
     * @value IHeroLocale|null
     */
    public locale : IHeroLocale|null = null;
    /**
     * observable of locale
     * @value BehaviorSubject<IHeroLocale|null>
     */
    public locale$ : BehaviorSubject<IHeroLocale|null> = new BehaviorSubject<IHeroLocale|null>(null);
    /**
     * observable of the locale as string
     * @value BehaviorSubject<string>
     */
    public localeString$ : BehaviorSubject<string> = new BehaviorSubject<string>('de-DE');

    constructor(
        @Inject(HERO_DEFAULT_LANGUAGE) private defaultLanguage : string,
        @Inject(HERO_DEFAULT_LOCATION) private defaultLocation : string,
    ) {
        // set the observable
        this.locale$.pipe(
            tap((locale) => this.locale = locale),
            tap(() => this.localeString$.next(this.getLocaleString()))
        ).subscribe();
    }

    /**
     * get all avalible locales
     *
     * @todo the data is loaded from a static file, this needs to be changed
     *       to a dynamic loading from a service.
     *
     * @returns IHeroLocation[]
     */
    getLocales(): IHeroLocation[] {
        return locationData.map((location : IHeroLocationData) => {
            // extract the properties from the location object
            const { code, name, native, currency, timezone, flag } = location;
            return {
                code, name, native, currency, timezone, flag,
                languages: languageData.filter((language : {code: string}) => location.languages.includes(language.code)),
            };
        });
    }

    /**
     * set locale
     *
     * @param language_segment can be a locale string (e.g. 'de-DE') or just a language string (like 'de' or 'en')
     * @param country_segment can be a country string (like 'DE') or null|undefined
     */
    setLocale(language_segment : string, country_segment? : string) : void {

        // split the locale into language and region
        let [language_split, country_split] = language_segment.split('-');
        if(!country_segment) {
            country_segment = (country_split || language_split).toLocaleUpperCase();
        }
        language_segment = language_split.toLocaleLowerCase();

        // find the location
        let location = locationData.find((loc : {code : string}) => loc.code === country_segment);
        let language = languageData.find((lang: {code : string}) => lang.code === language_segment);

        // check if the location is supported
        if(!location) return;

        // check if the language is supported by the location
        // if not, use the default language for the location
        if(!location.languages.find((lang:string) => lang === language_segment)) {
            language = languageData.find((lang : {code: string}) => lang.code === location.languages[0]) as IHeroLanguage;
            if(!language) return;
        }
        // extract the properties from the location object
        const { code, name, native, currency, timezone, flag } = location;

        this.locale$.next({
            code, name, native, currency, timezone, flag,
            language : language as IHeroLanguage,
        });
    }

    /**
     * get locale
     *
     * @returns IHeroLocale|null
     */
    getLocale() : IHeroLocale | null {
        return this.locale$.getValue();
    }

    /**
     * Loads the Location and Language from browser
     */
    loadLocale() : void {
        const browserLanguage = typeof navigator !== 'undefined'
            ? (navigator.language || navigator.languages?.[0] || this.defaultLocale)
            : this.defaultLocale;
        this.setLocale(browserLanguage);
    }

    private get defaultLocale() : string {
        return `${this.defaultLanguage}-${this.defaultLocation}`;
    }

    /**
     * gets the language
     *
     * @todo implement default language if no language is found
     *       from the injection token `HERO_DEFAULT_LANGUAGE`
     *
     * @returns String language code
     */
    getLanguageCode() : string {
        return this.getLocale()?.language.code || this.defaultLanguage;
    }

    /**
     * gets locale string
     */
    getLocaleString() : string{
        // if no locale is set, return the default locale
        const locale = this.getLocale();
        if(!locale) return `${this.defaultLanguage}-${this.defaultLocation}`;
        // extract the language and country code from the locale
        let language = this.getLanguageCode();
        let country = locale.code;
        // return the formatted locale string

        return `${language}-${country}`;
    }
}
